import { useState, type ChangeEvent } from 'react';
import { Plus, RefreshCw, Trash2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@rudi/ui/badge';
import { Button } from '@rudi/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@rudi/ui/dialog';
import { Input } from '@rudi/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@rudi/ui/table';
import { PageHeader, QueryState } from '../components/page';
import { useTenantTeams, useTenants } from '../api/hooks';
import {
  networkSummaries,
  recruitmentError,
  useAssignNetwork,
  useDeleteCode,
  useImportRecruitment,
  useRecruitmentOverview,
  useSyncRecruitment,
  type ActivityTotals,
  type NetworkSummary,
  type RecruitmentCode,
  type RecruitmentOverview,
  type UnclaimedSource,
} from '../api/recruitment';
import { QrBatchDialog } from './QrBatchDialog';

const SELECT =
  'h-9 rounded-3xl border border-input bg-background px-3 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

function Counts({ totals }: { totals: ActivityTotals }) {
  return (
    <>
      <TableCell className="text-right tabular-nums">{totals.scans}</TableCell>
      <TableCell className="text-right tabular-nums">{totals.chatsStarted}</TableCell>
      <TableCell className="text-right tabular-nums">{totals.patientMessages}</TableCell>
      <TableCell className="text-right tabular-nums">{totals.emailsSent}</TableCell>
    </>
  );
}

function CountHeads() {
  return (
    <>
      <TableHead className="text-right">Scans</TableHead>
      <TableHead className="text-right">Chats</TableHead>
      <TableHead className="text-right">Patient messages</TableHead>
      <TableHead className="text-right">Emails</TableHead>
    </>
  );
}

function syncText(overview: RecruitmentOverview): string {
  if (!overview.syncConfigured) {
    return 'The hourly sync from the patient chat is not configured in this Rudi.';
  }
  if (!overview.lastSyncedAt) return 'Rudi has not copied any counts from the patient chat yet.';
  return `Counts copied from the patient chat ${new Date(overview.lastSyncedAt).toLocaleString()}.`;
}

function AssignToTeam({ network }: { network: string }) {
  const tenants = useTenants();
  const [tenantId, setTenantId] = useState('');
  const teams = useTenantTeams(tenantId);
  const [teamId, setTeamId] = useState('');
  const assign = useAssignNetwork();

  const submit = () =>
    assign.mutate(
      { network, tenantId, teamId },
      {
        onSuccess: result =>
          toast.success(
            `${result.practicesCreated} practices added, ${result.practicesLinked} already there, ${result.linksCreated} code links created`
          ),
        onError: error => toast.error(recruitmentError(error)),
      }
    );

  return (
    <div className="flex flex-wrap items-center gap-2">
      <select
        aria-label={`Organisation for ${network}`}
        className={SELECT}
        value={tenantId}
        onChange={event => {
          setTenantId(event.target.value);
          setTeamId('');
        }}
      >
        <option value="">Organisation</option>
        {(tenants.data?.tenants ?? []).map(tenant => (
          <option key={tenant.id} value={tenant.id}>
            {tenant.name}
          </option>
        ))}
      </select>
      <select
        aria-label={`Team for ${network}`}
        className={SELECT}
        value={teamId}
        disabled={!tenantId}
        onChange={event => setTeamId(event.target.value)}
      >
        <option value="">Team</option>
        {(teams.data ?? [])
          .filter(team => team.status === 'active')
          .map(team => (
            <option key={team.id} value={team.id}>
              {team.displayName || team.name}
            </option>
          ))}
      </select>
      <Button size="sm" disabled={!teamId || assign.isPending} onClick={submit}>
        Add practices to team
      </Button>
    </div>
  );
}

/** Delete a code entered by mistake; its chats go back to the sources without a code. */
function RemoveCode({ code }: { code: RecruitmentCode }) {
  const [asking, setAsking] = useState(false);
  const remove = useDeleteCode();
  const confirm = () =>
    remove.mutate(code.id, {
      onSuccess: () => {
        toast.success(`${code.label} removed`);
        setAsking(false);
      },
      onError: error => toast.error(recruitmentError(error)),
    });
  return (
    <>
      <button
        type="button"
        aria-label={`Remove ${code.label}`}
        className="ml-1 align-middle text-muted-foreground hover:text-destructive"
        onClick={() => setAsking(true)}
      >
        <Trash2 className="inline h-3 w-3" />
      </button>
      {asking && (
        <Dialog open onOpenChange={open => !open && setAsking(false)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Remove {code.label}?</DialogTitle>
              <DialogDescription>
                {code.totals.chatsStarted > 0
                  ? `Its ${code.totals.chatsStarted} chats stop counting for the practice and show under sources without a code.`
                  : 'No chats have come through it yet.'}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAsking(false)}>
                Cancel
              </Button>
              <Button variant="destructive" disabled={remove.isPending} onClick={confirm}>
                Remove
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

function NetworkRow({ summary }: { summary: NetworkSummary }) {
  const [open, setOpen] = useState(false);
  const label = summary.network ?? 'No study center';
  return (
    <>
      <TableRow>
        <TableCell className="min-w-[10rem]">
          <button
            type="button"
            className="font-medium underline-offset-4 hover:underline"
            aria-expanded={open}
            onClick={() => setOpen(!open)}
          >
            {label}
          </button>
        </TableCell>
        <TableCell className="text-xs">
          {summary.teams.length
            ? summary.teams.map(team => `${team.tenantName} · ${team.teamName}`).join(', ')
            : summary.network
              ? 'No team yet'
              : 'Shown in Cortex only'}
        </TableCell>
        <TableCell className="text-right tabular-nums">{summary.sites.length}</TableCell>
        <TableCell className="text-right tabular-nums">{summary.codes.length}</TableCell>
        <Counts totals={summary.totals} />
      </TableRow>
      {open && (
        <TableRow>
          <TableCell colSpan={8} className="bg-muted/30">
            {summary.network && (
              <div className="mb-3">
                <AssignToTeam network={summary.network} />
              </div>
            )}
            <Table aria-label={`Practices of ${label}`}>
              <TableHeader>
                <TableRow>
                  <TableHead>Practice</TableHead>
                  <TableHead>Codes</TableHead>
                  <TableHead>Teams</TableHead>
                  <CountHeads />
                </TableRow>
              </TableHeader>
              <TableBody>
                {summary.sites.map(site => (
                  <TableRow key={site.id}>
                    <TableCell>
                      <div className="font-medium">
                        {site.name}
                        {site.isCenter && (
                          <Badge variant="secondary" className="ml-2">
                            Study center
                          </Badge>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {[
                          [site.street, site.houseNumber].filter(Boolean).join(' '),
                          [site.postalCode, site.city].filter(Boolean).join(' '),
                        ]
                          .filter(Boolean)
                          .join(', ')}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs">
                      {summary.codes
                        .filter(code => code.siteId === site.id)
                        .map((code, index) => (
                          <span key={code.id} className="whitespace-nowrap">
                            {index > 0 && ', '}
                            {code.label}
                            <RemoveCode code={code} />
                          </span>
                        ))}
                      {!summary.codes.some(code => code.siteId === site.id) && '—'}
                    </TableCell>
                    <TableCell className="text-xs">
                      {site.teams.map(team => team.teamName).join(', ') || '—'}
                    </TableCell>
                    <Counts totals={site.totals} />
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function OtherCodes({ codes }: { codes: RecruitmentCode[] }) {
  if (codes.length === 0) return null;
  return (
    <section className="mt-8">
      <h2 className="mb-2 text-lg font-semibold">Campaigns and test codes</h2>
      <div className="overflow-x-auto rounded-3xl border">
        <Table aria-label="Campaigns and test codes">
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Kind</TableHead>
              <TableHead>Note</TableHead>
              <CountHeads />
            </TableRow>
          </TableHeader>
          <TableBody>
            {codes.map(code => (
              <TableRow key={code.id}>
                <TableCell className="font-mono text-xs">
                  {code.label}
                  <RemoveCode code={code} />
                </TableCell>
                <TableCell className="text-xs">
                  {code.purpose === 'campaign' ? 'Campaign' : 'Test'}
                </TableCell>
                <TableCell className="text-xs">{code.note ?? ''}</TableCell>
                <Counts totals={code.totals} />
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </section>
  );
}

function Unclaimed({ sources }: { sources: UnclaimedSource[] }) {
  if (sources.length === 0) return null;
  return (
    <section className="mt-8">
      <h2 className="mb-1 text-lg font-semibold">Sources without a code</h2>
      <p className="mb-2 max-w-2xl text-xs text-muted-foreground">
        The patient chat recorded these source values, but no code claims them. Add a batch with a
        code for each one that belongs to a practice or campaign.
      </p>
      <div className="overflow-x-auto rounded-3xl border">
        <Table aria-label="Sources without a code">
          <TableHeader>
            <TableRow>
              <TableHead>Source</TableHead>
              <TableHead>Last day</TableHead>
              <CountHeads />
            </TableRow>
          </TableHeader>
          <TableBody>
            {sources.map(source => (
              <TableRow key={source.source}>
                <TableCell className="font-mono text-xs">{source.source}</TableCell>
                <TableCell className="text-xs">{source.lastDay}</TableCell>
                <Counts totals={source.totals} />
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </section>
  );
}

function ImportButton() {
  const importer = useImportRecruitment();
  const onFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    let payload: unknown;
    try {
      payload = JSON.parse(await file.text());
    } catch {
      toast.error(`${file.name} is not JSON`);
      return;
    }
    importer.mutate(payload, {
      onSuccess: result =>
        toast.success(
          `Imported ${result.sitesCreated} new and ${result.sitesUpdated} updated practices, ${result.codesCreated} new and ${result.codesUpdated} updated codes`
        ),
      onError: error => toast.error(recruitmentError(error)),
    });
  };
  return (
    <Button variant="outline" size="sm" asChild disabled={importer.isPending}>
      <label className="cursor-pointer">
        <Upload className="mr-1 h-4 w-4" />
        Import codes
        <input
          type="file"
          accept="application/json,.json"
          className="sr-only"
          aria-label="Import codes file"
          onChange={event => void onFile(event)}
        />
      </label>
    </Button>
  );
}

export function QrCodesPage() {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const overview = useRecruitmentOverview({
    startDate: startDate || undefined,
    endDate: endDate || undefined,
  });
  const sync = useSyncRecruitment();
  const [adding, setAdding] = useState(false);
  const data = overview.data;

  const syncAll = () =>
    sync.mutate(
      {},
      {
        onSuccess: result =>
          toast.success(`Copied ${result.sourceDays} source days, ${result.linkDays} link days`),
        onError: error => toast.error(recruitmentError(error)),
      }
    );

  return (
    <div>
      <PageHeader
        title="QR codes"
        description="Posters and flyers mailed to practices, which study center they feed, and the patient chats they started."
        actions={
          <>
            <Button size="sm" disabled={!data} onClick={() => setAdding(true)}>
              <Plus className="mr-1 h-4 w-4" />
              Add batch
            </Button>
            <ImportButton />
            <Button
              variant="outline"
              size="sm"
              disabled={sync.isPending || !data?.syncConfigured}
              onClick={syncAll}
            >
              <RefreshCw className="mr-1 h-4 w-4" />
              Sync all history
            </Button>
          </>
        }
      />
      <QueryState isLoading={overview.isLoading} error={overview.error}>
        {data === null || data === undefined ? (
          <p role="status" className="text-sm text-muted-foreground">
            This Rudi has no QR code statistics yet.
          </p>
        ) : (
          <>
            <div className="mb-4 flex flex-wrap items-end gap-3">
              <label className="flex flex-col gap-1 text-xs text-muted-foreground">
                From
                <Input
                  type="date"
                  aria-label="From"
                  value={startDate}
                  onChange={event => setStartDate(event.target.value)}
                  className="h-9 w-40"
                />
              </label>
              <label className="flex flex-col gap-1 text-xs text-muted-foreground">
                To
                <Input
                  type="date"
                  aria-label="To"
                  value={endDate}
                  onChange={event => setEndDate(event.target.value)}
                  className="h-9 w-40"
                />
              </label>
              <p className="pb-2 text-xs text-muted-foreground">{syncText(data)}</p>
            </div>
            <div className="overflow-x-auto rounded-3xl border">
              <Table aria-label="Study center networks">
                <TableHeader>
                  <TableRow>
                    <TableHead>Network</TableHead>
                    <TableHead>Teams</TableHead>
                    <TableHead className="text-right">Practices</TableHead>
                    <TableHead className="text-right">Codes</TableHead>
                    <CountHeads />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {networkSummaries(data).map(summary => (
                    <NetworkRow key={summary.network ?? ''} summary={summary} />
                  ))}
                  {data.codes.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} className="h-24 text-center text-muted-foreground">
                        No codes yet. Add a batch to start.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
            <OtherCodes codes={data.codes.filter(code => code.purpose !== 'practice')} />
            <Unclaimed sources={data.unclaimed} />
            {adding && <QrBatchDialog overview={data} onClose={() => setAdding(false)} />}
          </>
        )}
      </QueryState>
    </div>
  );
}
